import React, { useState, useEffect, useRef } from 'react';
import Modal from '../common/Modal';
import { fieldStyles } from '../../config/formStyles';
import {
  formatTasaForDisplay,
  formatNumberForDisplay,
  formatNumberForInput,
  parseAmountForState,
  parseTasaForState,
} from '../../utils/formatters';
import { operarPase, updateActivosUsados } from '../../services/pasesService';

/**
 * Modal del Trader para un pase, con dos modos:
 * - 'operar': marca el pase como Hecha (monto/tasa operados) y registra la
 *   cantidad usada por activo. Un solo update atómico.
 * - 'activos': solo la tabla de activos. Editable para el Trader (En Proceso /
 *   Hecha) para ir marcando lo usado; solo lectura en el resto de los casos.
 *
 * Props: isOpen, onClose, onSaved, pase, mode ('operar'|'activos'), readOnly, userName.
 */
const PaseOperarModal = ({ isOpen, onClose, onSaved, pase, mode = 'operar', readOnly = false, userName }) => {
  const [montoOperadoValue, setMontoOperadoValue] = useState('');
  const [tasaOperadaValue, setTasaOperadaValue] = useState('');
  const [usados, setUsados] = useState({});
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const didInit = useRef(false);

  const activos = pase?.activos || [];

  useEffect(() => {
    if (!isOpen) { didInit.current = false; return; }
    if (didInit.current) return;
    didInit.current = true;
    setFormError('');
    setIsSaving(false);
    if (!pase) return;
    setMontoOperadoValue(formatNumberForDisplay(pase.monto));
    setTasaOperadaValue(formatTasaForDisplay(pase.tasa));
    const initialUsados = {};
    (pase.activos || []).forEach(a => {
      initialUsados[a.id] = a.cantidadUsada ? formatNumberForInput(String(a.cantidadUsada)) : '';
    });
    setUsados(initialUsados);
  }, [isOpen, pase]);

  const handleUsadoChange = (id, value) => setUsados(prev => ({ ...prev, [id]: value }));

  // Construye el array de activos con la cantidad usada acotada a [0, cantidad].
  const buildActivos = () => activos.map(a => {
    const raw = parseFloat(parseAmountForState(String(usados[a.id] ?? ''))) || 0;
    const cantidadUsada = Math.min(Math.max(raw, 0), a.cantidad || 0);
    return { ...a, cantidadUsada };
  });

  const handleConfirmOperar = async () => {
    setIsSaving(true);
    setFormError('');
    try {
      await operarPase(
        pase,
        {
          montoOperado: parseFloat(parseAmountForState(montoOperadoValue)) || 0,
          tasaOperada: parseFloat(parseTasaForState(tasaOperadaValue)) || 0,
          activos: buildActivos(),
        },
        { userName }
      );
      onSaved?.();
      onClose?.();
    } catch (error) {
      console.error('Error al operar el pase:', error);
      setFormError('No se pudo confirmar la operación.');
      setIsSaving(false);
    }
  };

  const handleSaveActivos = async () => {
    setIsSaving(true);
    setFormError('');
    try {
      await updateActivosUsados(pase.id, buildActivos());
      onSaved?.();
      onClose?.();
    } catch (error) {
      console.error('Error al guardar los activos usados:', error);
      setFormError('No se pudieron guardar los activos.');
      setIsSaving(false);
    }
  };

  if (!isOpen || !pase) return null;

  const isOperar = mode === 'operar';
  const canEditUsado = !readOnly;

  const activosTable = (
    <div style={{ overflowX: 'auto', border: '1px solid #E5E7EB', borderRadius: '6px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead style={{ backgroundColor: '#F9FAFB' }}>
          <tr>
            <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontSize: '0.72rem', textTransform: 'uppercase', color: '#6B7281' }}>Especie</th>
            <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontSize: '0.72rem', textTransform: 'uppercase', color: '#6B7281' }}>Disponible (VN)</th>
            <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontSize: '0.72rem', textTransform: 'uppercase', color: '#6B7281' }}>Cantidad usada</th>
          </tr>
        </thead>
        <tbody>
          {activos.length === 0 && (
            <tr><td colSpan={3} style={{ padding: '1rem', textAlign: 'center', color: '#465163', fontStyle: 'italic' }}>Este pase no tiene activos cargados.</td></tr>
          )}
          {activos.map(a => (
            <tr key={a.id}>
              <td style={{ padding: '0.5rem 0.75rem', borderTop: '1px solid #E5E7EB', fontWeight: 600 }}>{a.especie}</td>
              <td style={{ padding: '0.5rem 0.75rem', borderTop: '1px solid #E5E7EB', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatNumberForDisplay(a.cantidad, 0)}</td>
              <td style={{ padding: '0.5rem 0.75rem', borderTop: '1px solid #E5E7EB', textAlign: 'right' }}>
                {canEditUsado ? (
                  <input
                    type="text"
                    inputMode="decimal"
                    value={usados[a.id] ?? ''}
                    onChange={(e) => handleUsadoChange(a.id, e.target.value)}
                    onBlur={(e) => {
                      const raw = parseFloat(parseAmountForState(e.target.value)) || 0;
                      const clamped = Math.min(Math.max(raw, 0), a.cantidad || 0);
                      handleUsadoChange(a.id, clamped ? formatNumberForInput(String(clamped)) : '');
                    }}
                    style={{ ...fieldStyles.input, textAlign: 'right', width: '120px', display: 'inline-block' }}
                    placeholder="0"
                  />
                ) : (
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumberForDisplay(a.cantidadUsada, 0) || '0'}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const footer = readOnly ? (
    <button type="button" onClick={onClose} style={fieldStyles.button}>Cerrar</button>
  ) : (
    <>
      <button type="button" onClick={onClose} style={{ ...fieldStyles.button, ...fieldStyles.secondaryButton }}>Cancelar</button>
      <button type="button" onClick={isOperar ? handleConfirmOperar : handleSaveActivos} style={fieldStyles.button} disabled={isSaving}>
        {isSaving ? 'Guardando...' : (isOperar ? 'Confirmar y Marcar como Hecha' : 'Guardar activos')}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      title={isOperar ? 'Confirmar Operación del Pase' : 'Activos del Pase'}
      maxWidth="640px"
      error={formError}
      footer={footer}
    >
      {isOperar && (
        <>
          <p style={{ margin: '0 0 1rem 0', color: '#374151' }}>
            Ingresá el monto y la tasa operados, y cuánto se usó de cada activo para el pase de <strong>{pase.cliente}</strong>.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
            <div>
              <label style={fieldStyles.label}>Monto Operado</label>
              <input type="text" inputMode="decimal" value={montoOperadoValue}
                onChange={(e) => setMontoOperadoValue(e.target.value)}
                onFocus={(e) => setMontoOperadoValue(formatNumberForInput(e.target.value))}
                onBlur={(e) => { const rounded = Math.ceil(parseFloat(parseAmountForState(e.target.value)) || 0); setMontoOperadoValue(formatNumberForDisplay(String(rounded))); }}
                style={fieldStyles.input} autoFocus />
            </div>
            <div>
              <label style={fieldStyles.label}>Tasa Operada (%)</label>
              <input type="text" inputMode="decimal" value={tasaOperadaValue}
                onChange={(e) => setTasaOperadaValue(e.target.value)}
                onFocus={(e) => setTasaOperadaValue(formatNumberForInput(e.target.value))}
                onBlur={(e) => { const parsed = parseTasaForState(e.target.value); setTasaOperadaValue(formatTasaForDisplay(parsed)); }}
                style={fieldStyles.input} />
            </div>
          </div>
        </>
      )}
      {activosTable}
    </Modal>
  );
};

export default PaseOperarModal;
